/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { FormControl, Grid, InputLabel, MenuItem } from "@mui/material";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import { GetProductsWithPolicyWording } from "../services/api/product-service";
import type { IProduct } from "../services/model/product";
import { getAccessToken, useAuth } from "../context/auth";
import { useConfirm } from "./dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { BrandingContext } from "@/branding/BrandingProvider";

interface SelectInputProps {
  style?: any;
  loading: boolean;
  updateDescription: (description: string, showOption: boolean) => void;
}

interface IButtonData {
  name: string;
  description: string;
  number: string;
  key: number;
}

const SelectInput: React.FC<SelectInputProps> = (props) => {
  const [buttons, setButtons] = useState<IButtonData[]>([]);
  const [product, setProduct] = useState("");
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const branding = useContext(BrandingContext);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setProduct(value);
    // choose your boolean rule; this uses “non-empty selection”
    props.updateDescription(value, !!value);
  };

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title,
      description: message,
    });
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await GetProductsWithPolicyWording(
        token,
        branding?.clientId
      );
      if (response) {
        const products = (response.data as unknown as IProduct[]) ?? [];
        if (products.length) {
          // build array correctly (don’t pre-size and then push)
          const filledArray: IButtonData[] = [];
          for (let i = 0; i < products.length; i++) {
            const productName = products[i]["name"] as string;
            const wording = products[i]["policyWording"] as string;
            const productNumber = products[i]["clickToDial"] as string;
            filledArray.push({
              key: i,
              name: productName,
              description: wording,
              number: productNumber,
            });
          }
          setButtons(filledArray);
        } else {
          showDialog(
            "Warning",
            "There are no policies available for your profile."
          );
        }
      } else {
        showDialog(
          "No Connection",
          "There seems to be a problem with your internet connection. Please check your connection and try again."
        );
      }
    } catch (err) {
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <div>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Grid container>
            <FormControl variant="outlined" fullWidth>
              <InputLabel id="product-label">Product Options</InputLabel>
              <Select
                labelId="product-label"
                id="product-select"
                value={product}
                onChange={handleChange}
                label="Product Options"
              >
                {buttons.map((buttonData) => (
                  <MenuItem key={buttonData.key} value={buttonData.description}>
                    {buttonData.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      {/* If you're on react-spinners v2+, prefer cssOverride over css */}
      <BrandLoader open={loading} />
    </div>
  );
};

export default SelectInput;
